import { useEffect, useRef, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal } from "../components/common";
import { toast } from "sonner";
import { PaperPlaneTilt, UsersThree, Plus, Trash, LockKey, PencilSimple, Paperclip, X, CheckCircle, Microphone, Stop } from "@phosphor-icons/react";
import { roleLabel } from "../lib/constants";

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [memberData, setMemberData] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [text, setText] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const endRef = useRef(null);
  const lastRef = useRef(null);

  const loadMembers = () => api.get("/chat/members")
    .then((r) => { setMemberData(r.data); setBlocked(false); })
    .catch((e) => { if (e.response?.status === 403) setBlocked(true); });

  const loadMessages = async (initial = false) => {
    try {
      const params = lastRef.current ? { after: lastRef.current } : {};
      const r = await api.get("/chat/messages", { params });
      if (r.data.length) {
        setMessages((prev) => (initial ? r.data : [...prev, ...r.data]));
        lastRef.current = r.data[r.data.length - 1].created_at;
      } else if (initial) {
        setMessages([]);
      }
    } catch (e) { if (e.response?.status === 403) setBlocked(true); }
  };

  useEffect(() => {
    loadMembers();
    loadMessages(true);
    const id = setInterval(() => loadMessages(false), 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const sendAudio = async (blob) => {
    const fd = new FormData();
    fd.append("text", "");
    fd.append("file", new File([blob], `nota-voz-${Date.now()}.webm`, { type: "audio/webm" }));
    try {
      const r = await api.post("/chat/messages", fd);
      setMessages((prev) => [...prev, r.data]); lastRef.current = r.data.created_at;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleRec = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => { const blob = new Blob(chunksRef.current, { type: "audio/webm" }); stream.getTracks().forEach((t) => t.stop()); sendAudio(blob); setRecording(false); };
      mr.start(); recRef.current = mr; setRecording(true);
    } catch (e) { toast.error("No se pudo acceder al micrófono"); }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t && !file) return;
    const fd = new FormData();
    fd.append("text", t);
    if (file) fd.append("file", file);
    setText(""); setFile(null);
    try {
      const r = await api.post("/chat/messages", fd);
      setMessages((prev) => [...prev, r.data]);
      lastRef.current = r.data.created_at;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteMsg = async (id) => {
    try { await api.delete(`/chat/messages/${id}`); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const startEdit = (m) => { setEditingId(m.id); setEditText(m.text); };
  const saveEdit = async (id) => {
    const t = editText.trim(); if (!t) return;
    try {
      const r = await api.put(`/chat/messages/${id}`, { text: t });
      setMessages((prev) => prev.map((m) => (m.id === id ? r.data : m)));
      setEditingId(null); setEditText("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openManage = async () => {
    setManageOpen(true);
    try { const r = await api.get("/chat/available-users"); setAvailable(r.data); } catch (err) { /* ignore */ }
  };
  const addMember = async (uid) => {
    try { await api.post("/chat/members", { user_id: uid }); toast.success("Integrante agregado"); setAvailable((a) => a.filter((u) => u.id !== uid)); loadMembers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeMember = async (uid) => {
    try { await api.delete(`/chat/members/${uid}`); toast.success("Integrante removido"); loadMembers(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (blocked) {
    return (
      <div>
        <PageHeader overline="Equipo" title="Chat del equipo" />
        <Card className="p-10 text-center" data-testid="chat-blocked">
          <LockKey size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Aún no formas parte del chat del equipo. Pídele al gerente general que te agregue.</p>
        </Card>
      </div>
    );
  }

  const canManage = memberData?.can_manage;
  const meId = memberData?.me || user?.id;

  return (
    <div>
      <PageHeader overline="Equipo" title="Chat del equipo" />
      <div className="grid lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 flex flex-col h-[70vh]" data-testid="chat-panel">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm mt-8">Aún no hay mensajes. ¡Escribe el primero!</p>
            ) : messages.map((m) => {
              const mine = m.user_id === meId;
              const editing = editingId === m.id;
              return (
                <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-primary text-white" : "bg-muted"}`}>
                    {!mine && <div className="text-xs font-semibold text-primary mb-0.5">{m.user_name}</div>}
                    {editing ? (
                      <div className="flex items-center gap-1">
                        <input data-testid={`chat-edit-input-${m.id}`} className="flex-1 text-sm text-slate-900 rounded-sm px-2 py-1" value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(m.id)} autoFocus />
                        <button onClick={() => saveEdit(m.id)} data-testid={`chat-edit-save-${m.id}`} className="text-white"><CheckCircle size={18} weight="fill" /></button>
                        <button onClick={() => { setEditingId(null); setEditText(""); }} className="text-white/80"><X size={16} /></button>
                      </div>
                    ) : (
                      <>
                        {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                        {m.attachment && ((m.attachment.content_type || "").startsWith("audio/")
                          ? <audio controls src={fileUrl(m.attachment.path)} data-testid={`chat-audio-${m.id}`} className="mt-1 max-w-[240px] h-9" />
                          : (m.attachment.content_type || "").startsWith("image/")
                          ? <a href={fileUrl(m.attachment.path)} target="_blank" rel="noreferrer"><img src={fileUrl(m.attachment.path)} alt={m.attachment.name} className="mt-1 max-h-48 rounded-sm border border-white/20" /></a>
                          : <a href={fileUrl(m.attachment.path)} target="_blank" rel="noreferrer" data-testid={`chat-file-${m.id}`} className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold underline break-all ${mine ? "text-white" : "text-primary"}`}><Paperclip size={13} /> {m.attachment.name}</a>)}
                      </>
                    )}
                    <div className={`flex items-center gap-2 text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                      <span>{new Date(m.created_at).toLocaleString("es-MX", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}{m.edited ? " · editado" : ""}</span>
                      {mine && !editing && (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5">
                          {m.text && <button onClick={() => startEdit(m)} data-testid={`chat-edit-${m.id}`} title="Editar"><PencilSimple size={13} /></button>}
                          <button onClick={() => deleteMsg(m.id)} data-testid={`chat-delete-${m.id}`} title="Borrar"><Trash size={13} /></button>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2">
            <label data-testid="chat-attach" className="cursor-pointer text-muted-foreground hover:text-primary flex-shrink-0" title="Adjuntar archivo">
              <Paperclip size={20} />
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            </label>
            <button type="button" onClick={toggleRec} data-testid="chat-mic" title={recording ? "Detener y enviar" : "Grabar nota de voz"} className={`flex-shrink-0 ${recording ? "text-danger animate-pulse" : "text-muted-foreground hover:text-primary"}`}>{recording ? <Stop size={20} weight="fill" /> : <Microphone size={20} />}</button>
            <div className="flex-1">
              <input data-testid="chat-input" className="w-full h-11 px-3 rounded-sm border border-input text-sm" placeholder="Escribe un mensaje…" value={text} onChange={(e) => setText(e.target.value)} />
              {file && <div className="text-xs text-primary mt-1 flex items-center gap-1"><Paperclip size={12} /> {file.name} <button type="button" onClick={() => setFile(null)} className="text-muted-foreground hover:text-danger"><X size={12} /></button></div>}
            </div>
            <button type="submit" data-testid="chat-send" className="inline-flex items-center justify-center h-11 w-12 bg-primary text-white rounded-sm hover:bg-primary/90 transition-colors duration-200 flex-shrink-0"><PaperPlaneTilt size={18} weight="fill" /></button>
          </form>
        </Card>

        <Card className="p-4 h-fit" data-testid="chat-members">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><UsersThree size={18} weight="fill" className="text-primary" /><span className="font-semibold text-sm">Integrantes</span></div>
            {canManage && <button onClick={openManage} data-testid="manage-members-btn" className="text-primary hover:underline text-xs font-semibold inline-flex items-center gap-1"><Plus size={14} weight="bold" /> Administrar</button>}
          </div>
          <div className="space-y-2">
            {(memberData?.members || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin integrantes.{canManage ? " Agrega al equipo con 'Administrar'." : ""}</p>
            ) : (memberData.members || []).map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <div><div className="font-medium">{m.name}</div><div className="text-xs text-muted-foreground">{roleLabel(m.role)}</div></div>
                {canManage && <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`} className="text-muted-foreground hover:text-danger"><Trash size={15} /></button>}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Modal open={manageOpen} onClose={() => setManageOpen(false)} title="Agregar integrantes al chat">
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground">Todos los usuarios ya son integrantes.</p>
        ) : (
          <div className="space-y-2">
            {available.map((u) => (
              <div key={u.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm">
                <div><div className="font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{roleLabel(u.role)} · {u.email}</div></div>
                <button onClick={() => addMember(u.id)} data-testid={`add-member-${u.id}`} className="text-primary hover:underline text-xs font-semibold inline-flex items-center gap-1"><Plus size={14} weight="bold" /> Agregar</button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
